import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { projects } from "../Data/projects";
import styled from "styled-components";
import { COLORS } from "../../../Constants/colors";
import {
  H2,
  ParagraphLg,
  SubHeading,
  Divider,
} from "../../../Constants/typography";
import { motion } from "framer-motion";

// ─── Styled Components ─────────────────────────────

const PageWrapper = styled.div`
  width: 100%;
  background: ${COLORS.background};
  color: ${COLORS.textPrimary};
`;

const HeroWrapper = styled.div`
  width: 100%;
  height: 500px;
  overflow: hidden;
  display: flex;
  justify-content: center;

  @media (max-width: 768px) {
    height: auto;
  }
`;

const HeroImage = styled.img`
  height: 100%;
  width: 100%;
  max-width: 950px;
  object-fit: cover;
  object-position: ${(props) =>
    props.focusTop ? "center" : "bottom right"};
  border-radius: 12px;
`;

const Content = styled.div`
  max-width: 950px;
  margin: 0 auto;
  padding: 60px 80px;

  @media (max-width: 768px) {
    padding: 32px 20px;
  }
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-bottom: 40px;
  text-decoration: none;
  font-weight: 600;
  font-size: 16px;
  color: ${COLORS.primary};
  position: relative;
  padding-left: 20px;

  &::before {
    content: "←";
    position: absolute;
    left: 0;
    top: 0;
    font-size: 18px;
    color: ${COLORS.primary};
  }

  &:hover {
    color: ${COLORS.secondary};

    &::before {
      color: ${COLORS.secondary};
    }
  }
`;

const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const Chip = styled.span`
  background: ${COLORS.backgroundLight};
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 1.0rem;
  color: ${COLORS.textPrimary};
  font-weight: 500;
  border: 1px solid ${COLORS.border};
`;

const ItemCard = styled.div`
  position: relative;
  background: ${COLORS.cardBackground};
  border-left: 6px solid
    ${(props) => (props.type === "challenge" ? `${COLORS.errorBorder}` : `${COLORS.successBorder}`)};
  padding: 24px;
  border-radius: 14px;
  margin-bottom: 20px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px ${COLORS.shadowColor};

  &:hover {
    transform: translateY(-4px);
    background: ${COLORS.cardHoverBackground};
  }
`;

const ItemTitle = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: ${(props) => (props.type === "challenge" ? `${COLORS.danger}` : `${COLORS.positive}`)};
  margin-bottom: 8px;
`;

const ItemDescription = styled(ParagraphLg)`
  color: ${COLORS.textSecondary};
  font-size: 1.1rem;
`;

const AccordionBlock = styled.div`
  border: 1px solid ${COLORS.border};
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 20px;
  background: ${COLORS.cardBackground};
`;

const AccordionHeader = styled.div`
  padding: 16px 20px;
  font-weight: 600;
  font-size: 1.2rem;
  color: ${COLORS.primary};
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  background: ${COLORS.backgroundLight};

  &:hover {
    background: ${COLORS.background};
  }

  span {
    font-size: 1.0rem;
    color: ${COLORS.textSecondary};
  }
`;

const AccordionContent = styled.div`
  padding: 20px;
  background: ${COLORS.cardBackground};
`;

const FullPageLoader = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  height: 100vh;
  width: 100vw;
  background: ${COLORS.background};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Spinner = styled.div`
  width: 60px;
  height: 60px;
  border: 6px solid ${COLORS.primary};
  border-top: 6px solid transparent;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// ─── Helper ────────────────────────────────────────

const MultiParagraph = ({ content }) => {
  if (Array.isArray(content)) {
    return content.map((p, idx) => (
      <ItemDescription key={idx} style={{ marginBottom: "16px" }}>
        {p}
      </ItemDescription>
    ));
  }
  return <ItemDescription>{content}</ItemDescription>;
};

const BulletPoints = ({ points }) => {
    if (!Array.isArray(points)) return null;
  
    return (
      <ul style={{ paddingLeft: "1.5rem" }}>
        {points.map((point, idx) => (
          <li key={idx} style={{ marginBottom: "10px", fontSize: "1.1rem", color: COLORS.textSecondary }}>
            {point}
          </li>
        ))}
      </ul>
    );
  };

// ─── Component ─────────────────────────────────────

const ProjectDetail = () => {
  const { id } = useParams();
  const project = projects.find((p) => p.id === id);
  const [openSection, setOpenSection] = useState("challenges");
  const challengeRef = useRef(null);
  const solutionRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);



  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // useEffect(() => {
  //   const img = new Image();
  //   img.src = project.image;
  //   img.onload = () => setImageLoaded(true);
  // }, [project.image]);

  const toggleAccordion = (section) => {
    setOpenSection((prev) => {
      const isOpening = prev !== section;
      if (isOpening) {
        setTimeout(() => {
          let ref = null;
          if (section === "challenges") ref = challengeRef.current;
          if (section === "solution") ref = solutionRef.current;
  
          if (ref) {
            const yOffset = -100; // adjust based on navbar height
            const y =
              ref.getBoundingClientRect().top + window.pageYOffset + yOffset;
  
            window.scrollTo({ top: y, behavior: "smooth" });
          }
        }, 100);
      }
      return prev === section ? null : section;
    });
  };

  if (!project)
    return (
      <PageWrapper>
        <Content>Project not found</Content>
      </PageWrapper>
    );

  // if (!imageLoaded) {
  //   return (
  //     <FullPageLoader>
  //       <Spinner />
  //     </FullPageLoader>
  //   );
  // }

  return (
    <PageWrapper>
      <HeroWrapper>
        <HeroImage 
            src={project.image} 
            alt={project.title} 
            focusTop={["maritime", "redha-al-ansari-exchange", "purplecore"].includes(id)}
        />
      </HeroWrapper>

      <Content>
        {/* <BackLink to="/projects">Back to Projects</BackLink> */}

        {/* <Section>
          <H2>{project.title}</H2>
          <Divider />
        </Section> */}

        {project.client && (
          <Section>
            <SubHeading>Client</SubHeading>
            <MultiParagraph content={project.client} />
          </Section>
        )}

        {project.summary && (
          <Section>
            <SubHeading>Summary</SubHeading>
            <MultiParagraph content={project.summary} />
          </Section>
        )}

        {(project.challenges || project.solution) && (
          <Section>
            {/* Challenges Accordion */}
            <AccordionBlock>
              <AccordionHeader ref={challengeRef} style={{ color: `${COLORS.danger}` }} onClick={() => toggleAccordion("challenges")}>
                🔴 Challenges
                <span>{openSection === "challenges" ? "▲" : "▼"}</span>
              </AccordionHeader>
              {openSection === "challenges" && (
                <AccordionContent>
                  {project.challenges?.map((item, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1 }}
                    >
                      <ItemCard type="challenge">
                        <ItemTitle type="challenge">{item.title}</ItemTitle>
                        <ItemDescription>{item.description}</ItemDescription>
                      </ItemCard>
                    </motion.div>
                  ))}
                </AccordionContent>
              )}
            </AccordionBlock>

            {/* Solutions Accordion */}
            <AccordionBlock>
              <AccordionHeader ref={solutionRef} style={{ color: `${COLORS.positive}` }} onClick={() => toggleAccordion("solution")}>
                🟢 Solution
                <span>{openSection === "solution" ? "▲" : "▼"}</span>
              </AccordionHeader>
              {openSection === "solution" && (
                <AccordionContent>
                  {project.solution?.map((item, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1 }}
                    >
                      <ItemCard type="solution">
                        <ItemTitle type="solution">{item.title}</ItemTitle>
                        <ItemDescription>{item.description}</ItemDescription>
                      </ItemCard>
                    </motion.div>
                  ))}
                </AccordionContent>
              )}
            </AccordionBlock>
          </Section>
        )}

        {project.benefit && (
          <Section>
            <SubHeading>Customer Benefit</SubHeading>
            <BulletPoints points={project.benefit} />
          </Section>
        )}

        {project.technology && (
          <Section>
            <SubHeading>Technology Used</SubHeading>
            <ChipList>
              {project.technology.map((tech, idx) => (
                <Chip key={idx}>{tech}</Chip>
              ))}
            </ChipList>
          </Section>
        )}
      </Content>
    </PageWrapper>
  );
};

export default ProjectDetail;

